import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/providers/auth-provider";

export interface PlanUsage {
  planName: string;
  planId: number | null;
  businessesUsed: number;
  businessesLimit: number;
  bankAccountsUsed: number;
  bankAccountsLimit: number;
  transactionsLimit: number;
}

export interface OnboardingStatus {
  hasSubscription: boolean;
  hasBusiness: boolean;
  hasBankConnected: boolean;
  isReady: boolean;
  planName: string;
}

export interface AdminPlan {
  id: number;
  name: string;
  description: string | null;
  monthlyPrice: string;
  yearlyPrice: string;
  features: string[] | null;
  maxBusinesses: number | null;
  maxTransactions: number | null;
  maxBankAccounts: number | null;
  stripePriceIdMonthly: string | null;
  stripePriceIdYearly: string | null;
  isActive: boolean;
  isPublic: boolean;
}

export function usePlanUsage() {
  return useQuery<PlanUsage>({
    queryKey: ["/api/subscriptions/usage"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/subscriptions/usage");
      return res.json();
    },
  });
}

export function useOnboardingStatus() {
  const { user } = useAuth();
  return useQuery<OnboardingStatus>({
    queryKey: ["/api/onboarding/status"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/onboarding/status");
      return res.json();
    },
    enabled: !!user,
  });
}

export function useAdminPlans() {
  return useQuery<AdminPlan[]>({
    queryKey: ["/api/admin/plans"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/admin/plans");
      return res.json();
    },
  });
}

export function usePublicPlans() {
  return useQuery<AdminPlan[]>({
    queryKey: ["/api/subscription-plans"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/subscription-plans");
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    },
  });
}

export function useCheckout() {
  return useMutation({
    mutationFn: async ({
      planId,
      billingCycle,
    }: {
      planId: number;
      billingCycle: "monthly" | "yearly";
    }) => {
      const res = await apiRequest("POST", "/api/subscriptions/create-checkout", {
        planId,
        billingCycle,
      });
      return res.json();
    },
    onSuccess: (data) => {
      if (data.sessionUrl) {
        window.location.href = data.sessionUrl;
      }
    },
  });
}
